
import FuelStopDetailView from '@components/fuel-stop/fuel-stop-detail/FuelStopDetailView.vue';
import AppHeader from '@components/new-styleguide/AppHeader.vue';
import { roundNumber } from '@util/numberFormat';
import { queryParamToObject } from '@util/parseQueryUtils';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import { LoadState } from '@util/types';
import { Address, FuelStopMutation, Pilot, SuggestedFuelStop, Trip } from '@clients/myTrips';
import LocationAddress from '@/models/LocationAddress';
import { RouterFrom, RouterQuery } from '@router/Routes';
import goBackWithFallback from '@util/goBackWithFallback';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import PositionMap from '@components/my-trips/PositionMap.vue';
import { Location } from '@/generated-clients/myTrips';
import { Location as RouterLocation } from 'vue-router'

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: "FuelStopAdd",
  components: {
    AppHeader,
    InfoBoxWarning,
    AppPage,
    AppContent,
    FuelStopDetailView,
    SettingsFooter,
    PositionMap
  }
})
export default class FuelStopAdd extends Vue {
  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getPilots')
  private pilots: Pilot[];

  @TriplogModule.Action('fetchSingleTrip')
  private fetchSingleTrip;

  @TriplogModule.Action('createFuelStop')
  private createFuelStop;

  @TriplogModule.Getter('getSingleTripStatus')
  private tripStatus: LoadState;

  @TriplogModule.Getter('getCreateFuelStopStatus')
  private fuelStopCreateStatus: LoadState;

  @TriplogModule.Action('fetchOldestTrip')
  private fetchOldestTrip;

  @RouterModule.State('from')
  private from: RouterFrom;

  @RouterModule.State('query')
  private query: RouterQuery;

  private disabled = false;

  private prevTrip: Trip = null;
  private oldestTrip: Trip = null;
  private errorMessage = '';

  private fuelStop: FuelStopMutation = {
    address: null,
    location: null,
    timestamp: new Date().toISOString(),
    litres: null,
    totalPrice: null,
    pilotId: null,
    fullTank: true
  };

  async created() {
    if (!this.queryVehicleId) {
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    }

    if (this.queryPrevTripId) {
      this.prevTrip = await this.fetchSingleTrip({ tripId: this.queryPrevTripId });
      this.fuelStop.address = this.defaultAddress;
      this.fuelStop.location = this.defaultLocation;
    } else if (this.querySuggestedFuelStop) {
      this.fillSuggestedValues();
    }

    this.fetchPilots();
    this.oldestTrip = await this.fetchOldestTrip({ vehicleId: this.queryVehicleId });
  }

  get backButton() {
    return { text: 'Zurück zu Fahrten', location: this.backLocation };
  }

  get backLocation(): RouterLocation {
    return { name: 'myTrips', query: { vehicle: this.queryVehicleId.toString() } };
  }

  get minTimestamp(): string {
    if (!this.oldestTrip || this.querySuggestedFuelStop) {
      return null;
    }

    const hasFuelStopBeforeOldestTrip = this.oldestTrip.fuelStops
        .some(fs => fs.timestamp <= this.oldestTrip.startInfo.timestamp);

    if (!hasFuelStopBeforeOldestTrip) {
      return null;
    }

    const minTime = new Date(this.oldestTrip.startInfo.timestamp);
    minTime.setMinutes(minTime.getMinutes() + 1);
    return minTime.toISOString();
  }

  get defaultLocation(): Location {
    return this.defaultLocationAddress.location || { latitude: 48.208442578127546, longitude: 16.374201141067804 };
  }

  get defaultAddress(): Address {
    return this.defaultLocationAddress.address;
  }

  get defaultLocationAddress(): LocationAddress {
    if (!this.prevTrip) {
      return { address: null, location: null };
    }
    const { endInfo, startInfo } = this.prevTrip;
    if (endInfo) {
      return { address: endInfo.address, location: endInfo.location }
    }
    return { address: startInfo.address, location: startInfo.location }
  }

  get isLoading(): boolean {
    return this.pilotsStatus === LoadState.Loading || this.tripStatus === LoadState.Loading;
  }

  get hasLoadError(): boolean {
    return this.pilotsStatus === LoadState.Error || this.tripStatus === LoadState.Error;
  }

  get hasInputError(): boolean {
    return !this.fuelStop.address
        || !this.fuelStop.location
        || !this.fuelStop.timestamp
        || !this.fuelStop.litres
        || this.fuelStop.litres <= 0
        || !this.fuelStop.totalPrice
        || new Date(this.fuelStop.timestamp) <= new Date(this.minTimestamp)
        || new Date(this.fuelStop.timestamp) > new Date();
  }

  get queryVehicleId(): number {
    return parseInt(this.query.vehicle as string);
  }

  get querySuggestedFuelStop(): SuggestedFuelStop {
    if (this.query.suggested) {
      return queryParamToObject(this.query.suggested as string);
    }
  }

  get queryPrevTripId(): number {
    return parseInt(this.query.prevTripId as string);
  }

  private fillSuggestedValues() {
    const suggested = this.querySuggestedFuelStop;

    this.fuelStop = { ...this.fuelStop, ...suggested };

    if (this.fuelStop.litres > 200) {
      this.fuelStop.litres = 200;
    }
    if (this.fuelStop.totalPrice > 400) {
      this.fuelStop.totalPrice = 400;
    }

    this.fuelStop.litres = roundNumber(this.fuelStop.litres, 2);
    this.fuelStop.totalPrice = roundNumber(this.fuelStop.totalPrice, 2);
  }

  private async handleFuelStopSubmit() {
    if (!this.hasInputError) {
      this.disabled = true;
      try {
        await this.createFuelStop({vehicleId: this.queryVehicleId, fuelStopMutation: this.fuelStop})
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } catch (error) {
        this.errorMessage = 'Fehler beim Speichern der Betankung. Bitte versuchen sie es später erneut.'
      } finally {
        this.disabled = false;
      }
    }
  }
}
